import React, { useState } from 'react';
import { useAuth } from '../../services/auth';
import { useFirebaseAuth } from '../../contexts/FirebaseAuthContext';
import { useToast } from '../../hooks/useToast';
import { cloudSync } from '../../services/sync/CloudSyncService';
import { LockClosedIcon } from '../Icons';
import * as encryption from '../../services/security/encryption';
import { doc, updateDoc, setDoc, getDoc } from 'firebase/firestore';
import { updatePassword } from 'firebase/auth';
import { auth, db } from '../../src/firebase';

/**
 * Валидация пароля (Вариант 1: Оптимальный)
 * Минимум 8 символов, 1 заглавная, 1 строчная, 1 цифра, 1 спецсимвол.
 */
const validatePasswordStrength = (password: string) => {
    const minLength = 8;
    const hasUpper = /[A-ZА-Я]/.test(password); // Support Cyrillic too
    const hasLower = /[a-zа-я]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(password);

    if (password.length < minLength) return 'Пароль должен быть не менее 8 символов.';
    if (!hasUpper) return 'Добавьте хотя бы одну заглавную букву.';
    if (!hasLower) return 'Добавьте хотя бы одну строчную букву.';
    if (!hasNumber) return 'Добавьте хотя бы одну цифру.';
    if (!hasSpecial) return 'Добавьте хотя бы один специальный символ (например: !@#$%).';

    return null; // Valid
};

export const ForcePasswordChange: React.FC = () => {
    const { currentUser } = useAuth();
    const { tenantId } = useFirebaseAuth();
    const { showToast } = useToast();

    // We assume the user just logged in with their temporary password, 
    // which CloudSyncService cached in preloadedPassphrase.
    // If not, we ask them for it.
    const [currentPassword, setCurrentPassword] = useState(cloudSync.getPreloadedPassphrase() || '');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setPasswordError(null);

        if (!currentPassword) {
            showToast('Введите текущий (временный) пароль.', 'error');
            return;
        }

        if (newPassword !== confirmPassword) {
            setPasswordError('Новые пароли не совпадают.');
            return;
        }

        const strengthError = validatePasswordStrength(newPassword);
        if (strengthError) {
            setPasswordError(strengthError);
            return;
        }

        if (!currentUser?.id || !tenantId) {
            showToast('Ошибка сессии: нет ID пользователя или организации', 'error');
            return;
        }

        setIsProcessing(true);
        try {
            // STEP 1: Using the OLD password, unwrap the DEK
            const securityRef = doc(db, 'organizations', tenantId, 'settings', 'security');
            const secSnap = await getDoc(securityRef);
            if (!secSnap.exists() || !secSnap.data().salt) {
                throw new Error("Не найдены настройки безопасности организации (Salt).");
            }
            const salt = secSnap.data().salt;

            const memberRef = doc(db, 'organizations', tenantId, 'members', currentUser.id);
            const memberSnap = await getDoc(memberRef);
            if (!memberSnap.exists() || !memberSnap.data().wrappedDEK) {
                // Technically possible if admin created the user but DID NOT issue a key yet.
                // In that case, we can just update the auth password.
                console.warn("Wrapped DEK not found for this user. Skipping key re-wrap.");
            } else {
                const oldWrappedDEK = memberSnap.data().wrappedDEK;

                try {
                    // Try to decrypt DEK with the old password
                    const rawDEK = await encryption.unwrapKey(oldWrappedDEK, currentPassword, salt);

                    // Re-wrap DEK with the NEW password
                    const newWrappedDEK = await encryption.wrapKey(rawDEK, newPassword, salt);

                    // Save new wrapped DEK to DB
                    await updateDoc(memberRef, { wrappedDEK: newWrappedDEK });
                } catch (unwrapErr) {
                    console.error("Failed to unwrap DEK. Incorrect current password?", unwrapErr);
                    throw new Error("Текущий пароль указан неверно. Не удалось расшифровать ключ.");
                }
            }

            // STEP 2: Update actual Firebase Auth password via Admin SDK (or specific cloud function)
            // Note: In client SDK you'd need the user to re-authenticate with credential.
            // Since we might not have the raw Firebase user object handy here for reAuth,
            // we use our authAdminService to update it via Cloud Function/REST API bypass if available,
            // OR we must ensure they are freshly signed in (which they are) and use updatePassword.

            if (!auth.currentUser) throw new Error("Пользователь не авторизован в Firebase");
            await updatePassword(auth.currentUser, newPassword);

            // STEP 3: Remove the 'requiresPasswordChange' flag
            await updateDoc(memberRef, { requiresPasswordChange: false });

            // STEP 4: Update CloudSync preloaded passphrase so auto-unlock still works
            cloudSync.setPreloadedPassphrase(newPassword);

            // Refresh context to enter the app
            window.location.reload();

            showToast('Пароль успешно изменен! Ключ перешифрован.', 'success');

        } catch (error: any) {
            console.error("Force password change error:", error);
            showToast(error.message || 'Произошла ошибка при смене пароля', 'error');
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col justify-center items-center p-4">
            <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden">
                <div className="p-8">
                    <div className="mx-auto w-16 h-16 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-500 rounded-full flex items-center justify-center mb-6">
                        <LockClosedIcon className="w-8 h-8" />
                    </div>

                    <div className="text-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Обновление пароля</h2>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            В целях безопасности вам необходимо изменить временный пароль, выданный администратором, на ваш личный.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                        <div className={`${cloudSync.getPreloadedPassphrase() ? 'hidden' : 'block'}`}>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Текущий (временный) пароль</label>
                            <input
                                type="password"
                                required={!cloudSync.getPreloadedPassphrase()}
                                value={currentPassword}
                                onChange={(e) => setCurrentPassword(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Введите текущий пароль"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Новый пароль</label>
                            <input
                                type="password"
                                required
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="..."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Повторите новый пароль</label>
                            <input
                                type="password"
                                required
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                placeholder="..."
                            />
                        </div>

                        {passwordError && (
                            <div className="p-3 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 text-sm rounded-lg border border-red-100 dark:border-red-800">
                                {passwordError}
                            </div>
                        )}

                        <div className="pt-2">
                            <button
                                type="submit"
                                disabled={isProcessing}
                                className="w-full py-3 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg shadow-sm transition-colors disabled:opacity-50 flex justify-center items-center"
                            >
                                {isProcessing ? (
                                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                ) : (
                                    'Сохранить и войти'
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-xs text-gray-500 dark:text-gray-400 text-center">
                        Пароль должен содержать от 8 символов, заглавные и строчные буквы, цифры и спецсимволы.
                    </div>
                </div>
            </div>
        </div>
    );
};
